import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { UserPlus, X, Check, Loader2 } from 'lucide-react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL,
  import.meta.env.VITE_SUPABASE_ANON_KEY
);

interface ShareButtonProps {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  nameInput: string;
  setNameInput: (name: string) => void;
  emailInput: string;
  setEmailInput: (email: string) => void;
  cityInput: string;
  setCityInput: (city: string) => void;
  countryInput: string;
  setCountryInput: (country: string) => void;
  subscribing: boolean;
  setSubscribing: (subscribing: boolean) => void;
  subscribed: boolean;
  setSubscribed: (subscribed: boolean) => void;
  emailError: string;
  setEmailError: (error: string) => void;
}

export function ShareButton({
  showModal,
  setShowModal,
  nameInput,
  setNameInput,
  emailInput,
  setEmailInput,
  cityInput,
  setCityInput,
  countryInput,
  setCountryInput,
  subscribing,
  setSubscribing,
  subscribed,
  setSubscribed,
  emailError,
  setEmailError
}: ShareButtonProps) {
  const portalContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!portalContainerRef.current) {
      const container = document.createElement('div');
      container.id = 'share-modal-portal';
      document.body.appendChild(container);
      portalContainerRef.current = container;
    }

    return () => {
      if (portalContainerRef.current && portalContainerRef.current.parentNode) {
        portalContainerRef.current.parentNode.removeChild(portalContainerRef.current);
        portalContainerRef.current = null;
      }
    };
  }, []);

  const getCoordinatesFromLocation = async (city: string, country: string) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?city=${encodeURIComponent(city)}&country=${encodeURIComponent(country)}&format=json&limit=1`
      );
      const data = await response.json();

      if (data && data.length > 0) {
        return {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon)
        };
      }

      return {
        lat: 42.3149,
        lng: -83.0364
      };
    } catch {
      return {
        lat: 42.3149,
        lng: -83.0364
      };
    }
  };

  const handleSubscribe = async () => {
    const name = nameInput.trim();
    const email = emailInput.trim().toLowerCase();
    const city = cityInput.trim();
    const country = countryInput.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!name) {
      setEmailError('Please enter a name');
      return;
    }

    if (!email) {
      setEmailError('Please enter an email address');
      return;
    }

    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    if (!city) {
      setEmailError('Please enter a city');
      return;
    }

    if (!country) {
      setEmailError('Please enter a country');
      return;
    }

    setSubscribing(true);
    setEmailError('');

    try {
      const coords = await getCoordinatesFromLocation(city, country);

      const { error } = await supabase
        .from('email_subscriptions')
        .insert({
          name,
          email,
          location: `${city}, ${country}`,
          latitude: coords.lat,
          longitude: coords.lng,
          city,
          country,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Toronto'
        });

      if (error) {
        if (error.code === '23505') {
          setEmailError('This email is already subscribed');
        } else {
          throw error;
        }
      } else {
        // Send welcome email to subscriber
        try {
          await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-welcome-email`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                name,
                email,
                city,
                country
              }),
            }
          );
        } catch (emailError) {
          console.error('Failed to send welcome email:', emailError);
          // Don't fail the subscription if email fails
        }

        // Notify admin of new subscriber
        try {
          await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-admin-new-subscriber`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                name,
                email,
                city,
                country
              }),
            }
          );
        } catch (adminError) {
          console.error('Failed to notify admin:', adminError);
          // Don't fail the subscription if admin notification fails
        }

        setSubscribed(true);
        setNameInput('');
        setEmailInput('');
        setCityInput('');
        setCountryInput('');
        setTimeout(() => {
          setSubscribed(false);
          setShowModal(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Error subscribing:', error);
      setEmailError('Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="fixed bottom-6 right-6 bg-gradient-to-r from-emerald-500 to-teal-600 text-white p-4 rounded-full shadow-2xl hover:shadow-emerald-500/50 transition-all hover:scale-110 z-[9999] group"
        aria-label="Subscribe Someone to Prayer Notifications"
      >
        <UserPlus className="w-6 h-6" />
        <span className="absolute bottom-full right-0 mb-2 px-3 py-1.5 bg-gray-900 text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          Add Email Subscription
        </span>
      </button>

      {showModal && portalContainerRef.current && createPortal(
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10000] flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowModal(false);
              setNameInput('');
              setEmailInput('');
              setCityInput('');
              setCountryInput('');
              setEmailError('');
              setSubscribed(false);
            }
          }}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative animate-in fade-in slide-in-from-bottom-4 duration-300" >
            <button
              onClick={() => {
                setShowModal(false);
                setNameInput('');
                setEmailInput('');
                setCityInput('');
                setCountryInput('');
                setEmailError('');
                setSubscribed(false);
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <UserPlus className="w-10 h-10 text-white" />
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Subscribe Someone to Prayer Notifications
              </h2>
              <p className="text-gray-600 mb-6">
                Gift them daily prayer time reminders with Quranic verses
              </p>

              {subscribed ? (
                <div className="bg-emerald-50 border-2 border-emerald-500 rounded-xl p-6">
                  <Check className="w-16 h-16 text-emerald-600 mx-auto mb-3" />
                  <p className="text-xl font-semibold text-emerald-900 mb-2">
                    Successfully Subscribed!
                  </p>
                  <p className="text-sm text-emerald-700">
                    جَزَاكَ اللهُ خَيْرًا • May Allah reward you
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-4 mb-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Name:
                      </label>
                      <input
                        type="text"
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value)}
                        placeholder="John Doe"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-emerald-500 text-gray-900"
                        disabled={subscribing}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email Address:
                      </label>
                      <input
                        type="email"
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        placeholder="john@example.com"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-emerald-500 text-gray-900"
                        disabled={subscribing}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        City:
                      </label>
                      <input
                        type="text"
                        value={cityInput}
                        onChange={(e) => setCityInput(e.target.value)}
                        placeholder="Windsor"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-emerald-500 text-gray-900"
                        disabled={subscribing}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Country:
                      </label>
                      <input
                        type="text"
                        value={countryInput}
                        onChange={(e) => setCountryInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSubscribe()}
                        placeholder="Canada"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-emerald-500 text-gray-900"
                        disabled={subscribing}
                      />
                    </div>

                    {emailError && (
                      <p className="text-red-600 text-sm text-left">{emailError}</p>
                    )}
                  </div>

                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 mb-6 border border-emerald-200">
                    <p className="text-sm text-emerald-900 font-medium mb-2">
                      What they'll receive:
                    </p>
                    <ul className="text-xs text-emerald-800 space-y-1">
                      <li className="flex items-start gap-2">
                        <span className="text-emerald-600 mt-0.5">✓</span>
                        <span>Daily prayer-time notifications for their location</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-emerald-600 mt-0.5">✓</span>
                        <span>Quranic verses and authentic Hadiths</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-emerald-600 mt-0.5">✓</span>
                        <span>Islamic events and special occasions</span>
                      </li>
                    </ul>
                  </div>

                  <button
                    onClick={handleSubscribe}
                    disabled={subscribing}
                    className="w-full py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg font-semibold hover:from-emerald-700 hover:to-teal-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {subscribing ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Subscribing...
                      </>
                    ) : (
                      <>
                        <UserPlus className="w-5 h-5" />
                        Subscribe Them Now
                      </>
                    )}
                  </button>

                  <p className="text-xs text-gray-500 mt-4">
                    Prayer times will be calculated based on their location
                  </p>
                </>
              )}
            </div>
          </div>
        </div>,
        portalContainerRef.current!
      )}
    </>
  );
}
